'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import Plot4AdminPanel from './components/Plot4AdminPanel';
import QuestTemplateAdmin from './components/QuestTemplateAdmin';

interface ResonanceRow {
  content_type: string;
  content_id: string;
  content_title: string;
  count: number;
}

interface WalkthroughRow {
  checkpoint_id: string;
  checkpoint_title: string;
  checkpoint_index: number;
  count: number;
}

interface RoleRow {
  caretaker_role: string;
  count: number;
}

const ADMIN_EMAILS = ['admin@haalproject.com'];

export default function AdminDashboardPage() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [topFrameworks, setTopFrameworks] = useState<ResonanceRow[]>([]);
  const [topVerses, setTopVerses] = useState<ResonanceRow[]>([]);
  const [topKeys, setTopKeys] = useState<ResonanceRow[]>([]);
  const [walkthroughData, setWalkthroughData] = useState<WalkthroughRow[]>([]);
  const [roleData, setRoleData] = useState<RoleRow[]>([]);
  const [totalBookmarks, setTotalBookmarks] = useState(0);
  const [totalResonance, setTotalResonance] = useState(0);
  const [totalWalkthrough, setTotalWalkthrough] = useState(0);
  const [activeAdminTab, setActiveAdminTab] = useState<'analytics' | 'quest-templates' | 'rss-admin' | 'mana-newsletter' | 'guardian-review'>('analytics');
  const supabase = createClient();

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      setUser(user);
      if (user && ADMIN_EMAILS.includes(user.email || '')) {
        setAuthorized(true);
        await loadAnalytics();
      }
      setLoading(false);
    });
  }, []);

  async function loadAnalytics() {
    // Top framework pages
    const { data: frameworkData } = await supabase
      .from('content_resonance')
      .select('content_type, content_id, content_title')
      .eq('content_type', 'framework_page');
    if (frameworkData) {
      const counts = countBy(frameworkData, 'content_id');
      setTopFrameworks(topN(frameworkData, counts, 10));
    }

    // Top verse clicks
    const { data: verseData } = await supabase
      .from('content_resonance')
      .select('content_type, content_id, content_title')
      .eq('resonance_type', 'verse_click');
    if (verseData) {
      const counts = countBy(verseData, 'content_id');
      setTopVerses(topN(verseData, counts, 10));
    }

    // Top key concepts
    const { data: keyData } = await supabase
      .from('content_resonance')
      .select('content_type, content_id, content_title')
      .eq('content_type', 'key_concept');
    if (keyData) {
      const counts = countBy(keyData, 'content_id');
      setTopKeys(topN(keyData, counts, 10));
    }

    // Walkthrough completions by checkpoint
    const { data: wtData } = await supabase
      .from('walkthrough_completions')
      .select('checkpoint_id, checkpoint_title, checkpoint_index');
    if (wtData) {
      const counts = countBy(wtData, 'checkpoint_id');
      const unique = Object.entries(counts).map(([id, count]) => {
        const row = wtData.find(r => r.checkpoint_id === id)!;
        return { checkpoint_id: id, checkpoint_title: row.checkpoint_title, checkpoint_index: row.checkpoint_index, count };
      }).sort((a, b) => a.checkpoint_index - b.checkpoint_index);
      setWalkthroughData(unique);
      setTotalWalkthrough(wtData.length);
    }

    // Role distribution
    const { data: roleRows } = await supabase
      .from('content_resonance')
      .select('caretaker_role')
      .neq('caretaker_role', '');
    if (roleRows) {
      const counts = countBy(roleRows, 'caretaker_role');
      setRoleData(Object.entries(counts).map(([role, count]) => ({ caretaker_role: role, count })).sort((a, b) => b.count - a.count));
    }

    // Totals
    const { count: bCount } = await supabase.from('bookmarks').select('*', { count: 'exact', head: true });
    setTotalBookmarks(bCount || 0);
    const { count: rCount } = await supabase.from('content_resonance').select('*', { count: 'exact', head: true });
    setTotalResonance(rCount || 0);
  }

  function countBy(arr: any[], key: string): Record<string, number> {
    return arr.reduce((acc, row) => {
      acc[row[key]] = (acc[row[key]] || 0) + 1;
      return acc;
    }, {});
  }

  function topN(arr: any[], counts: Record<string, number>, n: number): ResonanceRow[] {
    const seen = new Set<string>();
    const unique: ResonanceRow[] = [];
    for (const row of arr) {
      if (!seen.has(row.content_id)) {
        seen.add(row.content_id);
        unique.push({ content_type: row.content_type, content_id: row.content_id, content_title: row.content_title, count: counts[row.content_id] });
      }
    }
    return unique.sort((a, b) => b.count - a.count).slice(0, n);
  }

  const maxWalkthrough = Math.max(...walkthroughData.map(w => w.count), 1);
  const completionRate = walkthroughData.length > 0
    ? Math.round((walkthroughData[walkthroughData.length - 1]?.count / walkthroughData[0]?.count) * 100) || 0
    : 0;

  if (loading) return (
    <div style={{ background: '#060d12', minHeight: '100vh' }}>
      <Header />
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="font-mono text-xs uppercase tracking-widest" style={{ color: 'rgba(201,168,76,0.4)' }}>Loading…</div>
      </div>
    </div>
  );

  if (!user || !authorized) return (
    <div style={{ background: '#060d12', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        <div className="font-mono text-xs uppercase tracking-widest mb-4" style={{ color: 'rgba(201,168,76,0.4)' }}>Admin Access Required</div>
        <h1 className="font-mono text-2xl uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>Dashboard</h1>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, marginBottom: 20 }}>This area is restricted to project administrators.</p>
        <Link href="/" className="font-mono text-xs uppercase tracking-widest px-4 py-2" style={{ border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>← Return Home</Link>
      </div>
      <Footer />
    </div>
  );

  return (
    <div style={{ background: '#060d12', minHeight: '100vh' }}>
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-16">
        <div className="mb-10">
          <p className="font-mono text-xs tracking-widest uppercase mb-2" style={{ color: 'rgba(201,168,76,0.5)' }}>✦ Admin</p>
          <h1 className="font-mono text-3xl font-black uppercase tracking-tight mb-2" style={{ color: '#F5F0E8' }}>Admin Dashboard</h1>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, fontStyle: 'italic' }}>What resonates. What guides. What completes.</p>
        </div>

        {/* ── ADMIN TAB NAV ── */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 32, flexWrap: 'wrap' }}>
          {([
            { id: 'analytics', label: 'Analytics' },
            { id: 'quest-templates', label: 'Quest Templates' },
            { id: 'rss-admin', label: 'RSS Feed Admin' },
            { id: 'mana-newsletter', label: 'MANA Newsletter' },
            { id: 'guardian-review', label: 'Guardian Review' },
          ] as const).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveAdminTab(tab.id)}
              style={{
                background: 'none', border: 'none', padding: '12px 20px', cursor: 'pointer',
                color: activeAdminTab === tab.id ? '#D4A82A' : 'rgba(212,197,169,0.4)',
                borderBottom: activeAdminTab === tab.id ? '2px solid #D4A82A' : '2px solid transparent',
                fontSize: 13, fontWeight: activeAdminTab === tab.id ? 700 : 400,
                fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.05em',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── QUEST TEMPLATES TAB ── */}
        {activeAdminTab === 'quest-templates' && (
          <div style={{ margin: '0 -24px' }}>
            <QuestTemplateAdmin />
          </div>
        )}

        {/* ── RSS ADMIN TAB ── */}
        {activeAdminTab === 'rss-admin' && (
          <div>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, marginBottom: 16 }}>
              Manage verified RSS sources and monitor feed polling status.
            </p>
            <a href="/fisabilillah-admin" style={{ color: '#D4A82A', fontSize: 14, textDecoration: 'none', border: '1px solid rgba(212,168,42,0.3)', padding: '10px 20px', borderRadius: 8, display: 'inline-block' }}>
              Open RSS Feed Admin →
            </a>
          </div>
        )}

        {/* ── MANA NEWSLETTER TAB ── */}
        {activeAdminTab === 'mana-newsletter' && (
          <div>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, marginBottom: 16 }}>
              Anthropic-powered content discovery and newsletter composition for MANA.
            </p>
            <a href="/mana-newsletter" style={{ color: '#c9a84c', fontSize: 14, textDecoration: 'none', border: '1px solid rgba(201,168,76,0.3)', padding: '10px 20px', borderRadius: 8, display: 'inline-block' }}>
              Open MANA Newsletter Intelligence →
            </a>
          </div>
        )}

        {/* ── GUARDIAN REVIEW TAB ── */}
        {activeAdminTab === 'guardian-review' && (
          <div>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, marginBottom: 16 }}>
              Review community ecosystem observations — approve/reject submissions and toggle gallery visibility.
            </p>
            <a href="/guardian-review" style={{ display: 'inline-block', fontFamily: 'monospace', fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', padding: '8px 16px', borderRadius: 4, background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.25)', color: '#2d8a87', textDecoration: 'none' }}>
              ◎ Open Guardian Review Panel →
            </a>
          </div>
        )}

        {/* ── ANALYTICS TAB ── */}
        {activeAdminTab === 'analytics' && (
          <>
            {/* ── PLOT 4 ADMIN PANEL ── */}
            <div className="mb-12">
              <Plot4AdminPanel />
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {[
                { label: 'Total Bookmarks', value: totalBookmarks, color: '#c9a84c', icon: '◈' },
                { label: 'Resonance Events', value: totalResonance, color: '#2d8a87', icon: '⬡' },
                { label: 'Walkthrough Steps', value: totalWalkthrough, color: '#2d7a47', icon: '◎' },
                { label: 'Completion Rate', value: `${completionRate}%`, color: '#8a8ab8', icon: '✦' },
              ].map((stat, i) => (
                <div key={i} className="rounded-xl p-5" style={{ background: `${stat.color}06`, border: `1px solid ${stat.color}20` }}>
                  <div style={{ fontSize: 22, color: stat.color, marginBottom: 6 }}>{stat.icon}</div>
                  <div className="font-mono text-2xl font-black" style={{ color: stat.color }}>{stat.value}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest mt-1" style={{ color: 'rgba(212,197,169,0.4)' }}>{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Top Framework Pages */}
              <AnalyticsCard title="Framework Pages" subtitle="Most visited sections" color="#2d8a87" icon="⬡" rows={topFrameworks} />
              {/* Top Key Concepts */}
              <AnalyticsCard title="Key Concepts" subtitle="Most resonant Keys" color="#d4a82a" icon="⌘" rows={topKeys} />
            </div>

            {/* Top Verse Clicks */}
            <div className="mb-12">
              <AnalyticsCard title="Verse Links" subtitle="Most clicked Quranic references" color="#2d7a47" icon="☽" rows={topVerses} wide />
            </div>

            {/* Walkthrough Completion Funnel */}
            <div className="rounded-xl p-6 mb-12" style={{ background: 'rgba(45,138,135,0.04)', border: '1px solid rgba(45,138,135,0.15)' }}>
              <div className="flex items-center gap-2 mb-6">
                <span style={{ color: '#2d8a87', fontSize: 18 }}>◎</span>
                <div>
                  <h2 className="font-mono text-sm font-black uppercase tracking-wide" style={{ color: '#F5F0E8' }}>Guided Walkthrough Funnel</h2>
                  <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>Checkpoint completion rates</p>
                </div>
              </div>
              {walkthroughData.length === 0 ? (
                <p className="font-mono text-xs" style={{ color: 'rgba(212,197,169,0.3)' }}>No walkthrough data yet.</p>
              ) : (
                <div className="space-y-3">
                  {walkthroughData.map((w, i) => (
                    <div key={w.checkpoint_id} className="flex items-center gap-3">
                      <div className="font-mono text-[9px] w-4 text-right" style={{ color: 'rgba(212,197,169,0.4)' }}>{i + 1}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: '#d4c5a9' }}>{w.checkpoint_title}</span>
                          <span className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.4)' }}>{w.count}</span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(45,138,135,0.1)' }}>
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${(w.count / maxWalkthrough) * 100}%`, background: i === walkthroughData.length - 1 ? '#2d7a47' : '#2d8a87' }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Role Distribution */}
            <div className="rounded-xl p-6" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)' }}>
              <div className="flex items-center gap-2 mb-6">
                <span style={{ color: '#c9a84c', fontSize: 18 }}>◈</span>
                <div>
                  <h2 className="font-mono text-sm font-black uppercase tracking-wide" style={{ color: '#F5F0E8' }}>Caretaker Role Distribution</h2>
                  <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>Who is engaging with the platform</p>
                </div>
              </div>
              {roleData.length === 0 ? (
                <p className="font-mono text-xs" style={{ color: 'rgba(212,197,169,0.3)' }}>No role data yet.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {roleData.map(r => (
                    <div key={r.caretaker_role} className="rounded-lg p-3 text-center" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
                      <div className="font-mono text-xl font-black" style={{ color: '#c9a84c' }}>{r.count}</div>
                      <div className="font-mono text-[9px] uppercase tracking-widest mt-1" style={{ color: 'rgba(212,197,169,0.5)' }}>{r.caretaker_role.replace('-', ' ')}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function AnalyticsCard({ title, subtitle, color, icon, rows, wide = false }: {
  title: string; subtitle: string; color: string; icon: string;
  rows: { content_id: string; content_title: string; count: number }[];
  wide?: boolean;
}) {
  const max = Math.max(...rows.map(r => r.count), 1);
  return (
    <div className="rounded-xl p-6" style={{ background: `${color}04`, border: `1px solid ${color}15` }}>
      <div className="flex items-center gap-2 mb-5">
        <span style={{ color, fontSize: 18 }}>{icon}</span>
        <div>
          <h2 className="font-mono text-sm font-black uppercase tracking-wide" style={{ color: '#F5F0E8' }}>{title}</h2>
          <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>{subtitle}</p>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="font-mono text-xs" style={{ color: 'rgba(212,197,169,0.3)' }}>No data yet.</p>
      ) : (
        <div className={`space-y-2 ${wide ? 'grid md:grid-cols-2 gap-2 space-y-0' : ''}`}>
          {rows.map((r, i) => (
            <div key={r.content_id} className="flex items-center gap-2">
              <span className="font-mono text-[9px] w-4 text-right flex-shrink-0" style={{ color: 'rgba(212,197,169,0.3)' }}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-mono text-[10px] uppercase tracking-widest truncate" style={{ color: '#d4c5a9' }}>{r.content_title}</span>
                  <span className="font-mono text-[9px] flex-shrink-0 ml-2" style={{ color: 'rgba(212,197,169,0.4)' }}>{r.count}</span>
                </div>
                <div className="h-1 rounded-full overflow-hidden" style={{ background: `${color}10` }}>
                  <div className="h-full rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: color }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
